// Feature flags (boolean toggles)
export interface FeatureFlags {
  enablePreMatching: boolean;       // Speculative pre-matching for instant "Next"
  enableAIPartners: boolean;        // AI fallback during low traffic
  enablePrivacyMode: boolean;       // Forced TURN relay option
  enableNSFWDetection: boolean;     // Client-side NSFW frame detection
  enableVoiceMorph: boolean;        // Voice morphing in Stage 1-2
  enableFaceFilters: boolean;       // Face filters in Stage 2
  enablePushNotifications: boolean; // Web Push notifications
  enableStreakCounter: boolean;     // Gamification streak
  enableGreatConversation: boolean; // Mutual "great chat" signal
}

// Tuning parameters (numeric/string)
export interface TuningParams {
  stageVoiceDurationSec: number;     // Stage 1 duration (default: 30)
  stageFilteredDurationSec: number;  // Stage 2 duration (default: 90)
  matchTimeoutSec: number;           // Max wait before fallback match (default: 8)
  aiPartnerTriggerSec: number;       // Seconds before AI fallback (default: 15)
  rateLimitPerHour: number;          // Connections per IP per hour (default: 30)
  heartbeatIntervalMs: number;       // Client heartbeat interval (default: 30000)
  heartbeatTimeoutMs: number;        // Server disconnects after this (default: 90000)
  turnCredentialTTLSec: number;      // TURN credential lifetime (default: 3600)
  minOnlineForStart: number;         // Min users before showing "Start" vs "Schedule" (default: 20)
  maxReportsBeforeBan: number;       // Reports in window before auto-ban (default: 3)
  reportWindowMinutes: number;       // Rolling window for report counting (default: 60)
  pushNotificationMaxPerWeek: number; // Cap weekly push notifications (default: 2)
}

// Matching weights (floats 0-1)
export interface MatchingWeights {
  ageProximity: number;    // default: 0.25
  genderPreference: number; // default: 0.20
  geoProximity: number;    // default: 0.30
  languageMatch: number;   // default: 0.15
  skipRateHistory: number; // default: 0.10
}

// Ban escalation tiers
export interface BanEscalation {
  tier1DurationMinutes: number;  // default: 60 (1 hour)
  tier2DurationMinutes: number;  // default: 1440 (24 hours)
  tier3DurationMinutes: number;  // default: 10080 (7 days)
}

// Combined configuration type
export interface DuoCastConfig {
  features: FeatureFlags;
  tuning: TuningParams;
  matching: MatchingWeights;
  banEscalation: BanEscalation;
}

// Default configuration with all values
export const DEFAULT_CONFIG: DuoCastConfig = {
  features: {
    enablePreMatching: true,
    enableAIPartners: false,
    enablePrivacyMode: true,
    enableNSFWDetection: true,
    enableVoiceMorph: false,
    enableFaceFilters: false,
    enablePushNotifications: true,
    enableStreakCounter: true,
    enableGreatConversation: true,
  },
  tuning: {
    stageVoiceDurationSec: 30,
    stageFilteredDurationSec: 90,
    matchTimeoutSec: 8,
    aiPartnerTriggerSec: 15,
    rateLimitPerHour: 30,
    heartbeatIntervalMs: 30000,
    heartbeatTimeoutMs: 90000,
    turnCredentialTTLSec: 3600,
    minOnlineForStart: 20,
    maxReportsBeforeBan: 3,
    reportWindowMinutes: 60,
    pushNotificationMaxPerWeek: 2,
  },
  matching: {
    ageProximity: 0.25,
    genderPreference: 0.20,
    geoProximity: 0.30,
    languageMatch: 0.15,
    skipRateHistory: 0.10,
  },
  banEscalation: {
    tier1DurationMinutes: 60,
    tier2DurationMinutes: 1440,
    tier3DurationMinutes: 10080,
  },
};

// String literal union of all dot-path config keys
export type ConfigKey =
  // Feature flags
  | 'features.enablePreMatching'
  | 'features.enableAIPartners'
  | 'features.enablePrivacyMode'
  | 'features.enableNSFWDetection'
  | 'features.enableVoiceMorph'
  | 'features.enableFaceFilters'
  | 'features.enablePushNotifications'
  | 'features.enableStreakCounter'
  | 'features.enableGreatConversation'
  // Tuning parameters
  | 'tuning.stageVoiceDurationSec'
  | 'tuning.stageFilteredDurationSec'
  | 'tuning.matchTimeoutSec'
  | 'tuning.aiPartnerTriggerSec'
  | 'tuning.rateLimitPerHour'
  | 'tuning.heartbeatIntervalMs'
  | 'tuning.heartbeatTimeoutMs'
  | 'tuning.turnCredentialTTLSec'
  | 'tuning.minOnlineForStart'
  | 'tuning.maxReportsBeforeBan'
  | 'tuning.reportWindowMinutes'
  | 'tuning.pushNotificationMaxPerWeek'
  // Matching weights
  | 'matching.ageProximity'
  | 'matching.genderPreference'
  | 'matching.geoProximity'
  | 'matching.languageMatch'
  | 'matching.skipRateHistory'
  // Ban escalation
  | 'banEscalation.tier1DurationMinutes'
  | 'banEscalation.tier2DurationMinutes'
  | 'banEscalation.tier3DurationMinutes';

// Validation result type
export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// Validation function
export function validateConfig(partial: Partial<DuoCastConfig>): ValidationResult {
  const errors: string[] = [];

  // Validate feature flags (if present)
  if (partial.features) {
    const features = partial.features as Partial<FeatureFlags>;
    for (const [key, value] of Object.entries(features)) {
      if (value !== undefined && typeof value !== 'boolean') {
        errors.push(`features.${key} must be a boolean, got ${typeof value}`);
      }
    }
  }

  // Validate tuning parameters (if present)
  if (partial.tuning) {
    const tuning = partial.tuning as Partial<TuningParams>;

    // Check for negative values
    const nonNegativeFields: (keyof TuningParams)[] = [
      'stageVoiceDurationSec',
      'stageFilteredDurationSec',
      'matchTimeoutSec',
      'aiPartnerTriggerSec',
      'rateLimitPerHour',
      'heartbeatIntervalMs',
      'heartbeatTimeoutMs',
      'turnCredentialTTLSec',
      'minOnlineForStart',
      'maxReportsBeforeBan',
      'reportWindowMinutes',
      'pushNotificationMaxPerWeek',
    ];

    for (const field of nonNegativeFields) {
      if (tuning[field] !== undefined) {
        if (typeof tuning[field] !== 'number') {
          errors.push(`tuning.${field} must be a number, got ${typeof tuning[field]}`);
        } else if (tuning[field]! < 0) {
          errors.push(`tuning.${field} must be non-negative, got ${tuning[field]}`);
        }
      }
    }

    // Check for zero values that don't make sense
    if (tuning.stageVoiceDurationSec !== undefined && tuning.stageVoiceDurationSec === 0) {
      errors.push('tuning.stageVoiceDurationSec must be greater than 0');
    }
    if (tuning.stageFilteredDurationSec !== undefined && tuning.stageFilteredDurationSec === 0) {
      errors.push('tuning.stageFilteredDurationSec must be greater than 0');
    }
    if (tuning.heartbeatIntervalMs !== undefined && tuning.heartbeatIntervalMs === 0) {
      errors.push('tuning.heartbeatIntervalMs must be greater than 0');
    }
    if (tuning.heartbeatTimeoutMs !== undefined && tuning.heartbeatTimeoutMs === 0) {
      errors.push('tuning.heartbeatTimeoutMs must be greater than 0');
    }

    // Check logical relationships
    if (
      tuning.heartbeatTimeoutMs !== undefined &&
      tuning.heartbeatIntervalMs !== undefined &&
      tuning.heartbeatTimeoutMs <= tuning.heartbeatIntervalMs
    ) {
      errors.push('tuning.heartbeatTimeoutMs must be greater than tuning.heartbeatIntervalMs');
    }
  }

  // Validate matching weights (if present)
  if (partial.matching) {
    const matching = partial.matching as Partial<MatchingWeights>;
    const weightFields: (keyof MatchingWeights)[] = [
      'ageProximity',
      'genderPreference',
      'geoProximity',
      'languageMatch',
      'skipRateHistory',
    ];

    for (const field of weightFields) {
      if (matching[field] !== undefined) {
        if (typeof matching[field] !== 'number') {
          errors.push(`matching.${field} must be a number, got ${typeof matching[field]}`);
        } else if (matching[field]! < 0 || matching[field]! > 1) {
          errors.push(`matching.${field} must be between 0 and 1, got ${matching[field]}`);
        }
      }
    }

    // Check if all weights are present, they should sum to 1.0
    const allWeightsPresent = weightFields.every((field) => matching[field] !== undefined);
    if (allWeightsPresent) {
      const sum = weightFields.reduce((acc, field) => acc + (matching[field] || 0), 0);
      const tolerance = 0.0001; // Allow small floating point errors
      if (Math.abs(sum - 1.0) > tolerance) {
        errors.push(`matching weights must sum to 1.0, got ${sum.toFixed(4)}`);
      }
    }
  }

  // Validate ban escalation (if present)
  if (partial.banEscalation) {
    const banEscalation = partial.banEscalation as Partial<BanEscalation>;
    const banFields: (keyof BanEscalation)[] = [
      'tier1DurationMinutes',
      'tier2DurationMinutes',
      'tier3DurationMinutes',
    ];

    for (const field of banFields) {
      if (banEscalation[field] !== undefined) {
        if (typeof banEscalation[field] !== 'number') {
          errors.push(`banEscalation.${field} must be a number, got ${typeof banEscalation[field]}`);
        } else if (banEscalation[field]! <= 0) {
          errors.push(`banEscalation.${field} must be greater than 0, got ${banEscalation[field]}`);
        }
      }
    }

    // Check escalation order (each tier should be longer than the previous)
    if (
      banEscalation.tier1DurationMinutes !== undefined &&
      banEscalation.tier2DurationMinutes !== undefined &&
      banEscalation.tier1DurationMinutes >= banEscalation.tier2DurationMinutes
    ) {
      errors.push('banEscalation.tier2DurationMinutes must be greater than tier1DurationMinutes');
    }
    if (
      banEscalation.tier2DurationMinutes !== undefined &&
      banEscalation.tier3DurationMinutes !== undefined &&
      banEscalation.tier2DurationMinutes >= banEscalation.tier3DurationMinutes
    ) {
      errors.push('banEscalation.tier3DurationMinutes must be greater than tier2DurationMinutes');
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
